"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "../../utils";
import { CircleIcon } from "lucide-react";
import { Flex, Label } from "@suns/design-system";

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupButtonItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        `focus-visible:ring-ring aspect-square h-10 w-10 rounded-md border bg-gray-200
        text-sm shadow focus:outline-none focus-visible:ring-1
        disabled:cursor-not-allowed disabled:opacity-50
        radix-state-checked:bg-secondary-500 radix-state-checked:font-medium
        radix-state-checked:text-white print:h-7 print:w-7 print:shadow-none`,
        className
      )}
      {...props}
    >
      {props.children ?? props.value}
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

function RadioGroupItem({
  className,
  label,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item> & { label: string }) {
  return (
    <Label key={props.value} className="cursor-pointer">
      <Flex gap="xs" justify="center" align="center">
        <RadioGroupPrimitive.Item
          data-slot="radio-group-item"
          className={cn(
            `focus-visible:border-ring focus-visible:ring-ring/50
            aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40
            aria-invalid:border-destructive shadow-xs aspect-square size-4 shrink-0
            rounded-full border border-gray-400 text-primary outline-none
            transition-[color,box-shadow] focus-visible:ring-[3px]
            disabled:cursor-not-allowed disabled:opacity-50`,
            className
          )}
          {...props}
        >
          <RadioGroupPrimitive.Indicator
            data-slot="radio-group-indicator"
            className="relative flex items-center justify-center"
          >
            <CircleIcon className="absolute left-1/2 top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 fill-primary" />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
        <div className="grid gap-1.5 font-normal">
          <p className="text-sm font-medium leading-none">{label}</p>
        </div>
      </Flex>
    </Label>
  );
}

export { RadioGroup, RadioGroupButtonItem, RadioGroupItem };
